import { defaultCountries } from 'react-international-phone'

const SUPPORTED_COUNTRIES = new Set(defaultCountries.map((country) => country[1]))

/** Fallback when the browser locale has no region (e.g. bare "es" or "en"). */
const LANGUAGE_FALLBACK_COUNTRY = {
  ar: 'sa',
  de: 'de',
  en: 'us',
  es: 'es',
  fr: 'fr',
  hi: 'in',
  it: 'it',
  ja: 'jp',
  ko: 'kr',
  nl: 'nl',
  pl: 'pl',
  pt: 'br',
  ru: 'ru',
  tr: 'tr',
  zh: 'cn',
}

function getBrowserLocales() {
  if (typeof navigator === 'undefined') return []

  if (navigator.languages?.length) {
    return [...navigator.languages]
  }

  if (navigator.language) {
    return [navigator.language]
  }

  return []
}

function isSupportedCountry(countryCode) {
  return SUPPORTED_COUNTRIES.has(countryCode)
}

function parseRegionFromLocale(locale) {
  const normalized = locale.trim().replace(/_/g, '-')

  if (typeof Intl !== 'undefined' && Intl.Locale) {
    try {
      const intlLocale = new Intl.Locale(normalized)
      if (intlLocale.region?.length === 2) {
        return intlLocale.region.toLowerCase()
      }

      const maximized = intlLocale.maximize?.()
      if (maximized?.region?.length === 2) {
        return maximized.region.toLowerCase()
      }
    } catch {
      // ignore invalid locale strings
    }
  }

  const parts = normalized.split('-')
  const region = parts[parts.length - 1]

  if (/^[a-z]{2}$/i.test(region)) {
    return region.toLowerCase()
  }

  return null
}

function parseLanguageFromLocale(locale) {
  const normalized = locale.trim().replace(/_/g, '-')
  return normalized.split('-')[0]?.toLowerCase() ?? ''
}

/**
 * Resolves the default phone country (ISO2) from browser locale preferences.
 * Examples: pt-BR → br (+55), es-MX → mx (+52), en-GB → gb (+44).
 */
export function getPhoneCountryFromBrowser() {
  const locales = getBrowserLocales()

  for (const locale of locales) {
    const region = parseRegionFromLocale(locale)
    if (region && isSupportedCountry(region)) {
      return region
    }
  }

  for (const locale of locales) {
    const language = parseLanguageFromLocale(locale)
    const fallback = LANGUAGE_FALLBACK_COUNTRY[language]
    if (fallback && isSupportedCountry(fallback)) {
      return fallback
    }
  }

  return 'us'
}
